import type { ValidationIssue } from '../../../errors'
import type { ZodIssue } from '../types'
import { FORMAT_DESCRIPTIONS } from './defaults'
import type { FieldPlan } from './field-plan'
import { type AnyZod, inspect, shapeEntries } from './introspect'
import { labelOf } from './labels'

// Step 3's output, made readable. Zod's defaults describe the parser's disappointment rather than the
// author's mistake — "Invalid input: expected string, received undefined" — so each issue code is restated
// against the field's own `.label()`. An author-supplied message (`z.string().min(3, 'Too short')`) is left
// exactly as written; only zod's own wording is replaced.
//
// A failure can be DEEPER than the field: `address.city` inside an object, `tags.1` inside a list. Those are
// named by walking the declaration down the issue's own path, so the message names the property the user has
// to fix rather than the container it sits in.

// zod's `format` tokens, in the author's vocabulary. A format it does not name (a bare `.regex()`, say) has
// nothing worth restating, so it falls back to the generic wording.
// zod's `expected` tokens, as the phrase that completes "«Label» must be …". Spelled out so the wording is
// editable without composing articles.
const EXPECTED: Record<string, string> = {
  string : 'a string',
  number : 'a number',
  int    : 'an integer',
  bigint : 'a whole number',
  boolean: 'true or false',
  date   : 'a date',
  object : 'an object',
  array  : 'an array',
  // `z.json()`. Not a zod token — ./coerce.ts raises it when the submitted text will not parse.
  json   : 'valid JSON',
}

export const requiredMessage = (plan: FieldPlan): string => `«${ plan.label }» is required`

export const invalidTypeMessage = (plan: FieldPlan, expected: string): string =>
  `«${ plan.label }» must be ${ EXPECTED[expected] ?? expected }`

// What one issue is ABOUT: the deepest declared thing on its path, and the value that sits there.
interface IssueTarget {
  // How to name it: a nested property uses its own label; a list item names the list plus its position.
  label: string
  // The submitted value at that path — how "left empty" is told apart from "wrong type" below the top level,
  // where the absence pipeline never ran (it only normalizes the field, not the field's insides).
  value: unknown
  found: boolean
}

const isIndex = (segment: string): boolean => /^\d+$/.test(segment)

/**
 * Walk the field's declaration and the submitted value together, one path segment at a time.
 *
 * Stops early if the path leads somewhere the declaration does not describe (a passthrough object, say) and
 * reports the deepest thing it did resolve — a slightly less specific label beats a wrong one.
 */
function resolveTarget(plan: FieldPlan, segments: string[], fieldValue: unknown): IssueTarget {
  let schema: AnyZod | undefined = plan.schema
  let value: unknown = fieldValue
  let label = plan.label
  let found = true

  for (const segment of segments) {
    const core = schema ? inspect(schema).core : undefined
    const coreType = core ? inspect(schema as AnyZod).coreType : undefined

    value = value == null ? undefined : (value as Record<string, unknown>)[segment]

    if (coreType === 'array' && isIndex(segment)) {
      // A list item has no name of its own, so it borrows the list's and states its position. 1-based: the
      // message is read by whoever filled the form in, not by whoever indexed the array.
      label = `${ label } item ${ Number(segment) + 1 }`
      schema = core?._zod?.def?.element as AnyZod | undefined
      continue
    }

    const nested = shapeEntries(core).find(([name]) => name === segment)

    if (!nested) {
      found = false
      break
    }

    schema = nested[1]
    label = labelOf(segment, schema)
  }

  return { label, value, found }
}

/**
 * A range bound, as the author would recognise it.
 *
 * zod compares dates as instants, so `z.date().min(new Date('2024-01-01'))` reports its bound as
 * `1704067200000` — a number nobody typed. On a date field it is rendered back as an ISO string.
 */
function bound(value: unknown, plan: FieldPlan): string {
  if (plan.coreType !== 'date' || typeof value !== 'number') {
    return String(value)
  }

  const asDate = new Date(value)

  return Number.isNaN(asDate.getTime()) ? String(value) : asDate.toISOString()
}

// A zod issue carrying a custom message the author wrote — nothing to restate.
function isAuthored(issue: ZodIssue & { message?: string }): boolean {
  const message = issue.message ?? ''

  // Every zod default starts by naming the input or the constraint; an authored one is arbitrary prose.
  return !!message && !/^(Invalid|Too big|Too small|Unrecognized|Not multiple)/.test(message)
}

/**
 * Restate one zod issue for a field. `path` is the field key — issues from a nested object keep the zod path
 * appended, so a caller can still point at the exact property.
 */
export function toFieldIssue(
  plan: FieldPlan,
  issue: ZodIssue & { message?: string; expected?: string; minimum?: unknown; maximum?: unknown; values?: unknown[] },
  fieldValue?: unknown
): ValidationIssue {
  const segments = (issue.path ?? []).map(String).filter(Boolean)
  const path = segments.length ? `${ plan.name }.${ segments.join('.') }` : plan.name
  const code = issue.code ?? 'custom'
  const target = resolveTarget(plan, segments, fieldValue)
  // `plan` carries the top-level label; below the top level the resolved target carries the right one.
  const named = { ...plan, label: target.label }

  if (isAuthored(issue)) {
    return { path, code, message: issue.message as string }
  }

  if (code === 'invalid_type') {
    // Absence reaching validation means nothing was there to fall back on. At the top level the pipeline has
    // already normalized absence, so zod's `received` settles it; deeper down nothing normalized the value,
    // so the submitted value itself is what says whether the field was left empty or filled in wrongly.
    const received = (issue as { received?: string }).received
    const absent = segments.length
      ? target.found && target.value == null
      : received === 'undefined' || received === 'null' || issue.expected === undefined

    return absent
      ? { path, code, message: requiredMessage(named) }
      : { path, code, message: invalidTypeMessage(named, issue.expected ?? '') }
  }

  if (code === 'too_small') {
    return { path, code, message: `«${ target.label }» must be at least ${ bound(issue.minimum, plan) }` }
  }

  if (code === 'too_big') {
    return { path, code, message: `«${ target.label }» must be at most ${ bound(issue.maximum, plan) }` }
  }

  if (code === 'invalid_value') {
    // zod reports the accepted set as `values`.
    const values = (issue.values ?? []).map(String)

    return { path, code, message: `«${ target.label }» must be one of: ${ values.join(', ') }` }
  }

  if (code === 'invalid_format') {
    // The format's own description already says what shape is expected, so restate that rather than
    // inventing a second phrasing for the same fact.
    const described = FORMAT_DESCRIPTIONS[(issue as { format?: string }).format ?? '']

    return described
      ? { path, code, message: `«${ target.label }» is invalid — ${ described.replace(/^Expects /, 'expects ') }` }
      : { path, code, message: `«${ target.label }» has an invalid format` }
  }

  return { path, code, message: `«${ target.label }» is invalid` }
}
